import ApiService from './api';

/**
 * Analytics Service
 * Handles all analytics-related API requests
 */
const AnalyticsService = {
  /**
   * Get user analytics summary
   * @param {string} period - Period for summary (daily, weekly, monthly)
   * @param {string} token - Auth token
   * @returns {Promise} - Response data
   */
  getSummary: async (period, token) => {
    try {
      const response = await ApiService.get(`/api/analytics/summary?period=${period}`, {
        'Authorization': `Bearer ${token}`
      });
      
      if (!response.ok) {
        console.error('Analytics API error:', await response.text());
        return { success: false, error: `Error ${response.status}: ${response.statusText}` };
      }
      
      const data = await response.json();
      console.log('Analytics summary data received:', data);
      return { success: true, ...data };
    } catch (error) {
      console.error('Error fetching analytics summary:', error);
      return { success: false, error: error.message };
    }
  },

  /**
   * Get volume analytics
   * @param {string} timeframe - Timeframe for analytics (week, month, year)
   * @param {string} token - Auth token
   * @returns {Promise} - Response data
   */
  getVolumeAnalytics: async (timeframe, token) => {
    try {
      const response = await ApiService.get(`/api/analytics/volume?timeframe=${timeframe}`, {
        'Authorization': `Bearer ${token}`
      });
      
      if (!response.ok) {
        console.error('Volume analytics API error:', await response.text());
        return { success: false, error: `Error ${response.status}: ${response.statusText}` };
      }
      
      const data = await response.json();
      console.log('Volume analytics data received:', data);
      return { success: true, ...data };
    } catch (error) {
      console.error('Error fetching volume analytics:', error);
      return { success: false, error: error.message };
    }
  },

  /**
   * Get exercise progress analytics
   * @param {string} exerciseId - Exercise ID
   * @param {string} timeframe - Timeframe for analytics (week, month, year)
   * @param {string} token - Auth token
   * @returns {Promise} - Response data
   */
  getExerciseProgress: async (exerciseId, timeframe, token) => {
    try {
      const response = await ApiService.get(`/api/analytics/exercise/${exerciseId}?timeframe=${timeframe}`, {
        'Authorization': `Bearer ${token}`
      });
      
      if (!response.ok) {
        console.error('Exercise progress API error:', await response.text());
        return { success: false, error: `Error ${response.status}: ${response.statusText}` };
      }
      
      const data = await response.json();
      console.log('Exercise progress data received:', data);
      return { success: true, ...data };
    } catch (error) {
      console.error('Error fetching exercise progress:', error);
      return { success: false, error: error.message };
    }
  },

  /**
   * Get workout frequency analytics
   * @param {string} timeframe - Timeframe for analytics (week, month, year)
   * @param {string} token - Auth token
   * @returns {Promise} - Response data
   */
  getWorkoutFrequency: async (timeframe, token) => {
    const response = await ApiService.get(`/api/analytics/workout-frequency?timeframe=${timeframe}`, {
      'Authorization': `Bearer ${token}`
    });
    
    return response.json();
  },
  
  /**
   * Get user exercises analytics
   * @param {string} token - Auth token
   * @returns {Promise} - Response data
   */
  getUserExercises: async (token) => {
    const response = await ApiService.get('/api/exercises/user-exercises', {
      'Authorization': `Bearer ${token}`
    });
    
    return response.json();
  }
};

export default AnalyticsService;
